import React from "react";
import { useLocation, Navigate } from "react-router-dom";

/**
 * Global trailing-slash redirect.
 *
 * Google Search Console (2026-07) flagged several URLs where the same page
 * was indexed twice — once with a trailing slash, once without — as
 * "Duplicate without user-selected canonical". Even though React Router
 * routes match both forms and SEO.jsx emits a canonical to the no-slash
 * variant, Google sometimes fails to pick one and both stay in the index.
 *
 * This component runs at every location change: if the pathname ends with
 * "/" (and is not the site root "/"), it 301-style redirects to the
 * no-slash canonical, preserving search + hash so pricing filters and
 * on-page anchors continue to work.
 *
 * The redirect happens client-side; a hosting-level 301 at the ingress
 * layer would be even better, but this is a zero-config, fully portable
 * fallback that fixes the SEO signal for Googlebot on the second crawl.
 */
export default function TrailingSlashRedirect({ children }) {
  const { pathname, search, hash } = useLocation();

  if (pathname.length > 1 && pathname.endsWith("/")) {
    const stripped = pathname.replace(/\/+$/, "");
    return <Navigate to={`${stripped}${search}${hash}`} replace />;
  }

  return children;
}
